import { supabase, supabaseAdmin } from "./supabase";

interface CreateUserParams {
  email: string;
  name: string;
  specialization?: string;
  role: "MENTOR" | "MENTEE" | "ADMIN";
}

export class UserAdministrationError extends Error {
  constructor(
    message: string,
    public step: string,
    public originalError?: any
  ) {
    super(message);
    this.name = "UserAdministrationError";
  }
}

export async function createUser({
  email,
  name,
  specialization,
  role,
}: CreateUserParams) {
  console.log(
    `Starting user creation process for ${email} with role ${role}...`
  );

  try {
    // 1. Create auth user
    console.log("Step 1: Creating auth user...");
    const tempPassword = "test1234";
    const { data: authData, error: authError } =
      await supabaseAdmin.auth.admin.createUser({
        email,
        password: tempPassword,
        email_confirm: true,
        user_metadata: { name },
      });

    if (authError) {
      console.error("Auth user creation failed:", authError);
      throw new UserAdministrationError(
        `Auth user creation failed: ${authError.message}`,
        "auth_creation",
        authError
      );
    }

    if (!authData?.user?.id) {
      console.error("Auth response:", authData);
      throw new UserAdministrationError(
        "Failed to create auth user - no ID returned",
        "auth_creation"
      );
    }

    const userId = authData.user.id;
    console.log("Auth user created successfully:", {
      id: userId,
      email: authData.user.email,
      metadata: authData.user.user_metadata,
    });

    // 2. Create base user record
    console.log("Step 2: Creating base user record...");
    const { data: baseUserData, error: baseUserError } = await supabase
      .from("vincer_base_user")
      .insert({
        uuid: userId,
        name,
        email,
        date_joined: new Date().toISOString(),
      })
      .select()
      .single();

    if (baseUserError) {
      console.error("Base user creation failed:", baseUserError);
      await cleanupAuthUser(userId);
      throw new UserAdministrationError(
        `Base user creation failed: ${baseUserError.message}`,
        "base_user_creation",
        baseUserError
      );
    }

    console.log("Base user created successfully:", baseUserData);

    // 3. Create role-specific record if needed
    if (role === "MENTOR") {
      console.log("Step 3: Creating mentor record...");
      const { error: mentorError } = await supabase
        .from("mentor")
        .insert({
          uuid: crypto.randomUUID(),
          user_uuid: userId,
          specialization: specialization || "",
          meetings_held: 0,
        })
        .select()
        .single();

      if (mentorError) {
        console.error("Mentor record creation failed:", mentorError);
        await cleanupBaseUser(userId);
        await cleanupAuthUser(userId);
        throw new UserAdministrationError(
          `Mentor record creation failed: ${mentorError.message}`,
          "mentor_creation",
          mentorError
        );
      }
    } else if (role === "MENTEE") {
      console.log("Step 3: Creating mentee record...");
      const { error: menteeError } = await supabase
        .from("mentee")
        .insert({
          uuid: crypto.randomUUID(),
          user_uuid: userId,
        })
        .select()
        .single();

      if (menteeError) {
        console.error("Mentee record creation failed:", menteeError);
        await cleanupBaseUser(userId);
        await cleanupAuthUser(userId);
        throw new UserAdministrationError(
          `Mentee record creation failed: ${menteeError.message}`,
          "mentee_creation",
          menteeError
        );
      }
    }

    // 4. Add user role
    console.log(`Step 4: Adding ${role} role...`);
    const { error: roleError } = await supabase.from("user_roles").insert({
      uuid: crypto.randomUUID(),
      user_uuid: userId,
      role_name: role,
    });

    if (roleError) {
      console.error("Role assignment failed:", roleError);
      if (role === "MENTOR") {
        await cleanupMentorRecord(userId);
      } else if (role === "MENTEE") {
        await cleanupMenteeRecord(userId);
      }
      await cleanupBaseUser(userId);
      await cleanupAuthUser(userId);
      throw new UserAdministrationError(
        `Role assignment failed: ${roleError.message}`,
        "role_assignment",
        roleError
      );
    }

    console.log("User creation process completed successfully!");
    return { userId, email, name, role };
  } catch (error) {
    if (error instanceof UserAdministrationError) {
      throw error;
    }
    throw new UserAdministrationError(
      "Unexpected error during user creation",
      "unknown",
      error
    );
  }
}

async function cleanupAuthUser(userId: string) {
  try {
    await supabaseAdmin.auth.admin.deleteUser(userId);
  } catch (error) {
    console.error("Failed to cleanup auth user:", error);
  }
}

async function cleanupBaseUser(userId: string) {
  try {
    await supabase.from("vincer_base_user").delete().eq("uuid", userId);
  } catch (error) {
    console.error("Failed to cleanup base user:", error);
  }
}

async function cleanupMentorRecord(userId: string) {
  try {
    await supabase.from("mentor").delete().eq("user_uuid", userId);
  } catch (error) {
    console.error("Failed to cleanup mentor record:", error);
  }
}

async function cleanupMenteeRecord(userId: string) {
  try {
    await supabase.from("mentee").delete().eq("user_uuid", userId);
  } catch (error) {
    console.error("Failed to cleanup mentee record:", error);
  }
}
